import { Injectable, inject } from '@angular/core';
import { openDB, type IDBPDatabase } from 'idb';
import type { Ubicacion, FingerprintId, DeviceInfo } from '@core/remito.model';
import { LogService } from './log.service';

export type LocationSyncStatus = 'pending' | 'syncing' | 'failed' | 'synced';

export interface PendingLocation {
  id: string;
  ubicacion: Ubicacion;
  sessionId: string;
  fingerprintId?: FingerprintId;
  deviceInfo?: DeviceInfo;
  createdAt: number;
  status: LocationSyncStatus;
  lastAttemptAt?: number;
  error?: string;
}

interface SavePayload {
  id: string;
  ubicacion: Ubicacion;
  sessionId: string;
  fingerprintId?: FingerprintId;
  deviceInfo?: DeviceInfo;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class CheckInLocationDbService {
  private readonly DB_NAME = 'remitos-pwa-location';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'pending-locations';

  private db: IDBPDatabase | null = null;
  private readonly logger = inject(LogService);

  async init(): Promise<void> {
    if (this.db) {
      return;
    }

    this.db = await openDB(this.DB_NAME, this.DB_VERSION, {
      upgrade: db => {
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, {
            keyPath: 'id'
          });
          store.createIndex('status', 'status');
          store.createIndex('createdAt', 'createdAt');
        }
      }
    });
  }

  async savePending(payload: SavePayload): Promise<PendingLocation> {
    await this.ensureDb();

    this.logger.info('[CheckInLocationDb] Guardando check-in en IndexedDB', {
      hasId: !!payload.id,
      hasSessionId: !!payload.sessionId,
      hasFingerprintId: !!payload.fingerprintId,
      hasLocation: !!payload.ubicacion,
      hasDeviceInfo: !!payload.deviceInfo
    });

    const pending: PendingLocation = {
      id: payload.id,
      ubicacion: payload.ubicacion,
      sessionId: payload.sessionId,
      fingerprintId: payload.fingerprintId,
      deviceInfo: payload.deviceInfo,
      createdAt: Date.now(),
      status: 'pending',
      error: payload.error ?? 'Check-in guardado localmente pendiente de Check-out'
    };

    await this.db!.put(this.STORE_NAME, pending);
    this.logger.info('[CheckInLocationDb] Check-in guardado exitosamente en IndexedDB');

    return pending;
  }

  async getPending(): Promise<PendingLocation[]> {
    await this.ensureDb();

    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const index = tx.store.index('status');
    return index.getAll('pending');
  }

  async getById(id: string): Promise<PendingLocation | undefined> {
    await this.ensureDb();
    return this.db!.get(this.STORE_NAME, id);
  }

  async updateStatus(id: string, status: LocationSyncStatus, error?: string): Promise<void> {
    await this.ensureDb();
    const entry = await this.getById(id);
    if (!entry) {
      throw new Error(`Registro ${id} no encontrado`);
    }

    entry.status = status;
    entry.lastAttemptAt = Date.now();
    entry.error = error;

    await this.db!.put(this.STORE_NAME, entry);
  }

  async delete(id: string): Promise<void> {
    await this.ensureDb();
    await this.db!.delete(this.STORE_NAME, id);
  }

  async getCount(): Promise<number> {
    await this.ensureDb();
    const tx = this.db!.transaction(this.STORE_NAME, 'readonly');
    const index = tx.store.index('status');
    return index.count('pending');
  }

  async clearAll(): Promise<void> {
    await this.ensureDb();
    const tx = this.db!.transaction(this.STORE_NAME, 'readwrite');
    await tx.store.clear();
    await tx.done;
  }

  private async ensureDb(): Promise<void> {
    if (!this.db) {
      await this.init();
    }
  }
}
